/**
 * ConfirmDialog: the compact confirm, built on Modal (`variant="confirm"`).
 * Spec: `brand-redesign-docs/08-OVERLAYS.md`.
 *
 * A small centred card: a tinted icon badge (coral when `danger`, the
 * default; teal otherwise), the title, one line of body, then Cancel and the
 * action side by side. Sets `loading` while the async `onConfirm` runs.
 * Closes itself on success; surfaces an inline error if `onConfirm` rejects.
 */

import { AlertTriangle, Info } from "lucide-react";
import { useCallback, useId, useState, type ReactNode } from "react";

import { Button } from "@/primitives/ui/Button";

import { Modal } from "./Modal";

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  /** Sync or async. On success the dialog closes itself. */
  onConfirm: () => void | Promise<void>;
  title: ReactNode;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** When true (default), the badge and confirm button render coral. */
  danger?: boolean;
  /** Override the error message when `onConfirm` throws. */
  errorLabel?: string;
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  body,
  confirmLabel = "Delete",
  cancelLabel = "Cancel",
  danger = true,
  errorLabel = "Something went wrong. Try again.",
}: ConfirmDialogProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const titleId = `confirm-title-${useId()}`;

  const handleConfirm = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      await onConfirm();
      setLoading(false);
      onClose();
    } catch {
      setLoading(false);
      setError(errorLabel);
    }
  }, [onConfirm, onClose, errorLabel]);

  const dismiss = () => {
    if (loading) return;
    setError(null);
    onClose();
  };

  const Icon = danger ? AlertTriangle : Info;

  return (
    <Modal
      open={open}
      onClose={dismiss}
      variant="confirm"
      titleId={titleId}
      showCloseButton={false}
      dismissOnBackdrop={!loading}
      footer={
        <>
          <Button variant="secondary" size="lg" onClick={dismiss}>
            {cancelLabel}
          </Button>
          <Button variant={danger ? "danger" : "primary"} size="lg" onClick={handleConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="ov-confirm">
        <span className="ov-confirm__badge" data-tone={danger ? "danger" : undefined} aria-hidden>
          <Icon size={24} strokeWidth={1.75} />
        </span>
        <h2 id={titleId} className="ov-confirm__title">
          {title}
        </h2>
        {body ? <p className="ov-confirm__body">{body}</p> : null}
        {error ? (
          <p role="alert" className="ov-confirm__error">
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
